import React, { useEffect, useState, useRef } from 'react';
import { Button } from './ui/Button';
import { Plus, Calendar, Download, Users, Briefcase, AlertTriangle, LayoutGrid, Search, ChevronDown, ChevronLeft, ChevronRight, X, Mail, Sun, Moon, Menu } from 'lucide-react';
import { cn } from '../lib/utils';
import { Mitarbeiter, Projekt, Abteilung } from '../types';
interface HeaderProps {
  currentKw: number;
  onAddProjekt: () => void;
  onAddMitarbeiter: () => void;
  onExport: () => void;
  onKwChange: (kw: number) => void;
  stats?: {
    mitarbeiterCount: number;
    projektCount: number;
    konflikteCount: number;
  };
  mitarbeiter: Mitarbeiter[];
  projekte: Projekt[];
  onEmployeeClick: (employee: Mitarbeiter) => void;
  onProjectClick: (project: Projekt) => void;
  isDark: boolean;
  onToggleDark: () => void;
  onToggleSidebar: () => void;
}
export function Header({
  currentKw,
  onAddProjekt,
  onAddMitarbeiter,
  onExport,
  onKwChange,
  stats,
  mitarbeiter,
  projekte,
  onEmployeeClick,
  onProjectClick,
  isDark,
  onToggleDark,
  onToggleSidebar
}: HeaderProps) {
  const [showMitarbeiterDropdown, setShowMitarbeiterDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMitarbeiterDropdown(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const grouped = mitarbeiter.reduce((acc, m) => {
    if (!acc[m.abteilung]) acc[m.abteilung] = [];
    acc[m.abteilung].push(m);
    return acc;
  }, {} as Record<Abteilung, Mitarbeiter[]>);
  const query = searchQuery.trim().toLowerCase();
  const filteredMitarbeiter = query ? mitarbeiter.filter(m => m.name.toLowerCase().includes(query) || m.abteilung.toLowerCase().includes(query) || m.skills.some(s => s.toLowerCase().includes(query)) || m.email.toLowerCase().includes(query)) : [];
  const filteredProjekte = query ? projekte.filter(p => p.name.toLowerCase().includes(query) || p.projektnummer.toLowerCase().includes(query) || p.kunde.toLowerCase().includes(query) || p.phase.toLowerCase().includes(query)) : [];
  const hasResults = filteredMitarbeiter.length > 0 || filteredProjekte.length > 0;
  return <div className="flex flex-col z-20 shadow-sm relative" data-id="element-133">
      <header className="h-16 bg-background/80 backdrop-blur-md border-b px-4 sm:px-6 flex items-center justify-between flex-shrink-0 transition-colors gap-4" data-id="element-134">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-135">
          <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={onToggleSidebar} data-id="element-136">
            <Menu className="h-5 w-5" data-id="element-137" />
          </Button>

          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl overflow-hidden shadow-lg shadow-primary/20 flex items-center justify-center flex-shrink-0" data-id="element-138">
            <img src="https://cdn.magicpatterns.com/uploads/kd4roRxqwsaiLMQsYfWTFP/AZxTKdL0M29kQl9SQ-fUpQ-AZxTKdL1zoLvzFyzOkPHjA.jpg" alt="RessourcenPlaner" className="h-8 w-8 sm:h-10 sm:w-10 object-cover" data-id="element-139" />
          </div>
          <div className="hidden sm:block" data-id="element-140">
            <h1 className="font-bold text-lg sm:text-xl tracking-tight text-foreground leading-none" data-id="element-141">
              RessourcenPlaner
            </h1>
            <p className="text-[10px] sm:text-xs text-muted-foreground font-medium" data-id="element-142">
              Maschinenbau GmbH
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 flex-1 justify-end" data-id="element-143">
          {/* Universal Search - Collapsed on mobile */}
          <div ref={searchRef} className="relative hidden md:block" data-id="element-144">
            <div className="relative" data-id="element-145">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-146" />
              <input type="text" placeholder="Suche..." value={searchQuery} onChange={e => {
              setSearchQuery(e.target.value);
              setShowSearchResults(e.target.value.trim().length > 0);
            }} onFocus={() => query.length > 0 && setShowSearchResults(true)} className="h-9 w-40 lg:w-72 pl-9 pr-8 rounded-lg border border-border bg-muted/20 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-all" data-id="element-147" />
              {searchQuery && <button onClick={() => {
              setSearchQuery('');
              setShowSearchResults(false);
            }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-id="element-148">
                  <X className="h-3.5 w-3.5" data-id="element-149" />
                </button>}
            </div>

            {showSearchResults && query.length > 0 && <div className="absolute top-full mt-2 right-0 w-80 sm:w-96 bg-popover border rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto custom-scrollbar" data-id="element-150">
                {!hasResults && <div className="p-4 text-center text-sm text-muted-foreground" data-id="element-151">
                    Keine Ergebnisse für „{searchQuery}"
                  </div>}

                {filteredMitarbeiter.length > 0 && <div data-id="element-152">
                    <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30 border-b sticky top-0" data-id="element-153">
                      <Users className="h-3 w-3 inline mr-1.5" data-id="element-154" />
                      Mitarbeiter ({filteredMitarbeiter.length})
                    </div>
                    {filteredMitarbeiter.map(emp => <button key={emp.id} onClick={() => {
                onEmployeeClick(emp);
                setShowSearchResults(false);
                setSearchQuery('');
              }} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors" data-id="element-155">
                        <div className="h-7 w-7 rounded-full bg-muted border border-border flex items-center justify-center text-[10px] font-bold text-muted-foreground flex-shrink-0" data-id="element-156">
                          {emp.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-157">
                          <div className="text-sm font-medium truncate" data-id="element-158">
                            {emp.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate" data-id="element-159">
                            {emp.abteilung} • {emp.skills.join(', ')}
                          </div>
                        </div>
                        {emp.verfuegbarkeit < 100 && <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950 px-1.5 py-0.5 rounded" data-id="element-160">
                            {emp.verfuegbarkeit}%
                          </span>}
                      </button>)}
                  </div>}

                {filteredProjekte.length > 0 && <div data-id="element-161">
                    <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30 border-b sticky top-0" data-id="element-162">
                      <Briefcase className="h-3 w-3 inline mr-1.5" data-id="element-163" />
                      Projekte ({filteredProjekte.length})
                    </div>
                    {filteredProjekte.map(p => <button key={p.id} onClick={() => {
                onProjectClick(p);
                setShowSearchResults(false);
                setSearchQuery('');
              }} className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors" data-id="element-164">
                        <div className="w-3 h-3 rounded-full shadow-sm flex-shrink-0" style={{
                  backgroundColor: p.farbe
                }} data-id="element-165" />
                        <div className="flex-1 min-w-0" data-id="element-166">
                          <div className="text-sm font-medium truncate" data-id="element-167">
                            {p.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground" data-id="element-168">
                            {p.projektnummer} • {p.kunde}
                          </div>
                        </div>
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', p.status === 'Aktiv' ? 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400' : p.status === 'Pausiert' ? 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-400' : 'bg-muted text-muted-foreground')} data-id="element-169">
                          {p.status}
                        </span>
                      </button>)}
                  </div>}
              </div>}
          </div>

          {/* KW Navigator */}
          <div className="flex items-center gap-1 bg-secondary/50 rounded-full border border-border" data-id="element-170">
            <button onClick={() => onKwChange(Math.max(1, currentKw - 1))} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground" data-id="element-171">
              <ChevronLeft className="h-4 w-4" data-id="element-172" />
            </button>
            <div className="flex items-center gap-2 px-2 hidden sm:flex" data-id="element-173">
              <Calendar className="h-4 w-4 text-primary" data-id="element-174" />
              <span className="text-sm font-medium text-muted-foreground whitespace-nowrap" data-id="element-175">
                <span className="text-foreground font-bold bg-background px-2 py-0.5 rounded shadow-sm border border-border/50" data-id="element-176">
                  KW {currentKw}
                </span>
              </span>
            </div>
            <div className="flex items-center px-1 sm:hidden" data-id="element-177">
              <span className="text-xs font-bold" data-id="element-178">KW {currentKw}</span>
            </div>
            <button onClick={() => onKwChange(Math.min(52, currentKw + 1))} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground" data-id="element-179">
              <ChevronRight className="h-4 w-4" data-id="element-180" />
            </button>
          </div>

          <div className="h-8 w-px bg-border hidden sm:block" data-id="element-181" />

          {/* Dark Mode Toggle */}
          <button onClick={onToggleDark} className="h-9 w-9 rounded-lg border border-border bg-muted/20 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex-shrink-0" title={isDark ? 'Light Mode' : 'Dark Mode'} data-id="element-182">
            {isDark ? <Sun className="h-4 w-4" data-id="element-183" /> : <Moon className="h-4 w-4" data-id="element-184" />}
          </button>

          <div className="flex items-center gap-2 sm:gap-3" data-id="element-185">
            <Button variant="ghost" size="icon" onClick={onExport} className="text-muted-foreground hover:text-foreground hidden sm:flex" title="Export CSV" data-id="element-186">
              <Download className="h-4 w-4" data-id="element-187" />
            </Button>
            <Button onClick={onAddProjekt} size="sm" className="shadow-md shadow-primary/10 hover:shadow-primary/20 transition-all px-2 sm:px-4" data-id="element-188">
              <Plus className="h-4 w-4 sm:mr-2" data-id="element-189" />
              <span className="hidden sm:inline" data-id="element-190">Projekt</span>
            </Button>
            <Button onClick={onAddMitarbeiter} variant="secondary" size="sm" className="shadow-sm px-2 sm:px-4 hidden md:flex" data-id="element-191">
              <Plus className="h-4 w-4 sm:mr-2" data-id="element-192" />
              <span className="hidden sm:inline" data-id="element-193">Mitarbeiter</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Bar - Scrollable on mobile */}
      {stats && <div className="h-10 bg-muted/30 border-b px-4 sm:px-6 flex items-center gap-4 sm:gap-8 text-xs font-medium text-muted-foreground transition-colors overflow-x-auto no-scrollbar whitespace-nowrap" data-id="element-194">
          <div ref={dropdownRef} className="relative" data-id="element-195">
            <button onClick={() => setShowMitarbeiterDropdown(!showMitarbeiterDropdown)} className="flex items-center gap-2 hover:text-foreground transition-colors rounded-md px-2 py-1 -mx-2 -my-1 hover:bg-muted/50" data-id="element-196">
              <Users className="h-3.5 w-3.5" data-id="element-197" />
              <span data-id="element-198">
                <strong className="text-foreground" data-id="element-199">
                  {stats.mitarbeiterCount}
                </strong>{' '}
                Mitarbeiter
              </span>
              <ChevronDown className={cn('h-3 w-3 transition-transform', showMitarbeiterDropdown && 'rotate-180')} data-id="element-200" />
            </button>

            {showMitarbeiterDropdown && <div className="absolute top-full mt-2 left-0 w-80 bg-popover border rounded-lg shadow-xl z-50 py-2 max-h-[400px] overflow-y-auto custom-scrollbar" data-id="element-201">
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b mb-1" data-id="element-202">
                  Alle Mitarbeiter
                </div>
                {Object.entries(grouped).map(([dept, employees]) => <div key={dept} data-id="element-203">
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30 mt-1" data-id="element-204">
                      {dept} ({employees.length})
                    </div>
                    {employees.map(emp => <button key={emp.id} onClick={() => {
              onEmployeeClick(emp);
              setShowMitarbeiterDropdown(false);
            }} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors" data-id="element-205">
                        <div className="h-7 w-7 rounded-full bg-muted border border-border flex items-center justify-center text-[10px] font-bold text-muted-foreground flex-shrink-0" data-id="element-206">
                          {emp.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-207">
                          <div className="text-sm font-medium truncate" data-id="element-208">
                            {emp.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-2" data-id="element-209">
                            <span className="flex items-center gap-1" data-id="element-210">
                              <Mail className="h-2.5 w-2.5" data-id="element-211" />
                              {emp.email}
                            </span>
                          </div>
                        </div>
                        {emp.verfuegbarkeit < 100 && <span className="text-[10px] font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950 px-1.5 py-0.5 rounded" data-id="element-212">
                            {emp.verfuegbarkeit}%
                          </span>}
                      </button>)}
                  </div>)}
              </div>}
          </div>

          <div className="w-px h-3 bg-border flex-shrink-0" data-id="element-213" />
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-214">
            <Briefcase className="h-3.5 w-3.5" data-id="element-215" />
            <span data-id="element-216">
              <strong className="text-foreground" data-id="element-217">{stats.projektCount}</strong>{' '}
              aktive Projekte
            </span>
          </div>
          <div className="w-px h-3 bg-border flex-shrink-0" data-id="element-218" />
          <div className={cn('flex items-center gap-2 transition-colors flex-shrink-0', stats.konflikteCount > 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400')} data-id="element-219">
            <AlertTriangle className="h-3.5 w-3.5" data-id="element-220" />
            <span data-id="element-221">
              <strong className={cn('px-1.5 py-0.5 rounded text-[10px] mr-1', stats.konflikteCount > 0 ? 'bg-destructive text-destructive-foreground' : 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400')} data-id="element-222">
                {stats.konflikteCount}
              </strong>
              Konflikte
            </span>
          </div>
        </div>}
    </div>;
}